import { Logger } from '@visiba/logger';
import { Singleton } from '@visiba/instantiation';
import { EventEmitter } from '@visiba/std';
import { PUBLIC_SUB_URL_PATH, TRANSLATION_SET } from '../../constants';
import { DateService } from './date.service';
import { LicenceHolderService } from './licence_holder.service';
import { LocationSelectorService } from './location_selector.service';
import { RouterService } from './router.service';
import { RouteNotAccessible, UnitService } from './unit.service';
import { UserSettingsService } from './user_settings.service';
import { RegisterRecentVisitService } from './register_recent_visit.service';
import { RecentReceptionsApiService } from '@api/recent_receptions_api.service';
import { UnitsApiService } from '@api/units_api.service';
import { FeatureFlagService } from './feature_flag/mod';
import { AuthService, ReadonlyUser } from './auth/mod';
import { UnitModel, UserType } from '@api/generated/models';
import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import { LicenceHolderApiService } from '@api/licence_holder_api.service';
import { PublicBaseApiModel } from '@public/src/api/models/PublicBaseApiModel';

@Singleton()
export class StartupService {
	private readonly logger = new Logger('Startup Angular/React');

	public readonly events = new EventEmitter<{
		bootStateChange: BootState;
	}>();

	/**
	 * State representing how the application's health is for the first invokes.
	 */
	private internalBootState: BootState = BootState.Pristine;
	public get bootState(): BootState {
		return this.internalBootState;
	}
	public set bootState(state: BootState) {
		// private set bootState(state: BootState) { // injection issue. TS incompatibility.
		this.internalBootState = state;
		this.events.emit('bootStateChange', state);
		this.logger.logInformation(`🚩 Boot state changed to ${BootState[state]}`);
	}

	constructor(
		private readonly licenceHolderService: LicenceHolderService,
		private readonly userSettingsService: UserSettingsService,
		private readonly unitService: UnitService,
		private readonly authService: AuthService,
		private readonly routerService: RouterService,
		private readonly locationSelectorService: LocationSelectorService,
		private readonly dateService: DateService,
		private readonly registerRecentVisitService: RegisterRecentVisitService,
		private readonly recentReceptionsApiService: RecentReceptionsApiService,
		private readonly unitsApiService: UnitsApiService,
		private readonly featureToggleService: FeatureFlagService,
		private readonly licenceHolderApiService: LicenceHolderApiService,
	) {
		// Empty
	}

	/**
	 * Starts the application
	 * ---
	 * Initializing and distributing necessary data through globally dependant
	 * services. While not all data is required for the application to render, a
	 * portion is and that data is under a "Critical Data" section. The way the
	 * application will be able to determine when it may render is through a
	 * "bootState". This boot state is mutated through the flow of this method,
	 * and everything under "Routeable" should not invoke any React tree that's
	 * sensitive to the critical data existing.
	 *
	 * To make the experience better for the end user, keeping the amount of
	 * Critical Data down is important for us. Especially when the application
	 * is a SPA (Single Page Application) for the time being and the user needs to
	 * fetch both a main.js bundle and the booting data. We'll need to do some
	 * classic kluringar eventually to keep the load time for mobile devices as
	 * fast as possible - either smarter SPA handling or going SSR hybrid.
	 */
	public async boot(
		loadedData?: Partial<{
			patientBaseResult: PublicBaseApiModel;
			featureToggles: Record<string, boolean>;
			unitServiceResult: UnitModel;
			getTranslationSetResult: Record<string, string>;
		}>,
	): Promise<StartupService> {
		if (this.bootState !== BootState.Pristine) {
			throw new StartupServiceAlreadyBootedError('application may only be booted once');
		}

		try {
			this.logger.logInformation('Adds "authenticated" interceptor');
			this.authService.addInterceptor({
				triggerOn: 'authenticated',
				fn: this.loadRecentlyVisited.bind(this),
			});

			this.bootState = BootState.NonFunctional;

			const urlAlias = this.getUrlAlias();

			if (loadedData?.featureToggles == null) {
				this.logger.logWarning('Needs to refetch Feature Toggles (this is a bug)');
				await this.featureToggleService.load(urlAlias);
			}

			let data: PublicBaseApiModel;
			if (loadedData?.patientBaseResult) {
				this.logger.logInformation('Derived licenceHolder data');
				data = loadedData.patientBaseResult;
			} else {
				this.logger.logWarning('Needs to refetch licenceHolder (this is a bug)');
				const patientBaseResult = await this.unitsApiService.getLicenseHolderData(urlAlias);
				data = patientBaseResult.data;
			}
			await this.licenceHolderService.loadFromAlias(urlAlias, data);

			this.logger.logInformation('Refreshes user settings for Angular/React application');
			this.userSettingsService.loadSettings();
			this.userSettingsService.setLanguage();

			await this.unitService.load(this.licenceHolderService.rootUnitId, loadedData?.unitServiceResult);

			if (loadedData?.getTranslationSetResult) {
				this.logger.logInformation('Derived translation set');
				I18nAndL10n.insertTranslations(loadedData?.getTranslationSetResult);
			} else {
				this.logger.logWarning('Needs to refetch translation set (this is a bug)');
				try {
					const { data: translationSet } = await this.licenceHolderApiService.getTranslationSet(
						this.licenceHolderService.id,
						TRANSLATION_SET,
						this.userSettingsService.getSettings().selectedLanguage,
					);
					I18nAndL10n.insertTranslations(translationSet);
				} catch (error: unknown) {
					this.logger.logError('failed to fetch translations, reason: {reason}', (error as Error).message);
				}
			}

			await this.dateService.loadLocale(this.userSettingsService.getSettings().selectedLanguage);

			this.bootState = BootState.HasCriticalInformation;

			await this.authService.fetchActiveIdentity();

			this.setSelectedLocation(location.pathname).catch((error) => {
				let message = 'unknown';

				if (error instanceof RouteNotAccessible) {
					message = error.message;
				}

				this.logger.logError('failed to set location, reason: {reason}', message);
			});

			this.bootState = BootState.Routeable;
			this.bootState = BootState.Completed;

			return this;
		} catch (error: unknown) {
			this.bootState = BootState.NonFunctional;
			this.logger.logFatal('failed to boot application, reason: {reason}', (error as Error).message);

			throw error;
		}
	}

	/**
	 * Enables Selected Location updates on a history push level
	 * ---
	 * Since we're semi offloading "global application state" away from a React
	 * tree, we can't sync the Reception Location that the user is currently on
	 * with React hooks without getting unwanted race conditions. This is because
	 * React works asynchronous with its reconciliation algorithm (Fiber) while
	 * the "global application state" side is mostly synchronous. The issues that
	 * occurs otherwise is id miss matches and we don't want that. Thus this is a
	 * part of the application.
	 */
	public useImperativeHistoryListener(): StartupService {
		// Needs to create an history if we're going to listen to it.
		this.routerService.createBrowserHistory();

		this.routerService.history.listen((state) => {
			// "setLocationByRoute" will fail if the booting state is not in "Routeable".
			if (this.bootState >= BootState.Routeable) return;
			this.setSelectedLocation(state.location.pathname);
		});

		return this;
	}

	private async loadRecentlyVisited(user: ReadonlyUser | null): Promise<void> {
		// Ignored this if not signed in
		if (user == null) return;
		// Ignored this if outsider
		if (user.userType === UserType.Outsider) return;

		this.logger.logDebug('Took action on the "authenticated" interceptor');

		let latestVisitedReceptionId = await this.registerRecentVisitService.dispatchLatestRegistered();
		if (latestVisitedReceptionId == null) {
			const { data } = await this.recentReceptionsApiService.getRecentlyVisited(1);
			latestVisitedReceptionId = data[0]?.ReceptionId ?? null;
		}

		if (latestVisitedReceptionId != null) {
			const unit = this.unitService.getUnitNodeInTreeByReceptionId(latestVisitedReceptionId);

			await this.locationSelectorService.commitByUnitId(unit.id);
		}
	}

	private async setSelectedLocation(pathname: string): Promise<void> {
		const normalizePathname = this.unitService.normalizePathnameToBackTraceableRoute(pathname);
		const unitId = this.unitService.backTraceUnitIdFromRoute(normalizePathname);
		const unit = this.unitService.getUnitNodeInTree(unitId);
		if (!unit.isEntrance && unit.parent != null) {
			this.logger.logDebug(
				`Exited route "${normalizePathname}" early, can't do routing on non-entrances (non-root if not enterprise)`,
			);

			return;
		}

		await this.locationSelectorService.commitByRoute(normalizePathname);
	}

	private getUrlAlias(): string {
		let urlAlias = location.pathname.replace(`/${PUBLIC_SUB_URL_PATH}/`, '');
		if (urlAlias.charAt(0) == '/') {
			urlAlias = urlAlias.substring(1);
		}
		urlAlias = urlAlias.split('/')[0];

		return urlAlias;
	}
}

export enum BootState {
	Pristine = 0,
	NonFunctional = 1,
	HasCriticalInformation = 2,
	Routeable = 3,
	Completed = 32,
}

export class StartupServiceAlreadyBootedError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'StartupServiceAlreadyBootedError';
		Object.setPrototypeOf(this, StartupServiceAlreadyBootedError.prototype);
	}
}
